/**
 * Event-payload builders for the commit funnel (T26 — conversion telemetry
 * baseline). Same event names + property keys are used both before and after
 * the Confirm-step removal flag flips; only the `source_step` attribute differs.
 *
 * Dashboard query (Looker / GA4) — pre/post comparison:
 *   funnel: pageview(source=confirm|guarantee|personalize)
 *         → booking_commit_attempt(filtered by source_step)
 *         → reservation_complete (success)  OR  booking_commit_failure (error)
 *
 * Abandonment is derived: arrivals - attempts per session.
 */

import { ANALYTICS_EVENTS } from './analyticsEvents';
import type { AnalyticsEvent } from './types';

/**
 * Backend-error codes the commit funnel may surface verbatim. These are the
 * same code-shaped strings used in `useReservationCommit`'s
 * `backendErrorMessages` mapping table. Anything outside this set is treated
 * as unverified text and either truncated (for debug-ability) or dropped.
 *
 * Shared between `categorizeCommitError` and `safeErrorCode`.
 *
 * Its membership is exactly `PRE_PAYMENT_BACKEND_CODES` union
 * `TIP_STALE_CODES` from `@features/booking/services/commit/shared`, kept as a
 * DELIBERATE duplicate rather than an import: analytics does not depend on
 * `features/`, and a runtime edge for seven strings is not worth it. The cost
 * is that a new code must be added in both places — miss it here and the code
 * falls to the `unknown` bucket, which is what happened to the tip codes.
 */
const KNOWN_BACKEND_ERROR_CODES = new Set<string>([
  'RESTAURANT_INACTIVE',
  'BLOCKED_USER_SUBTITLE',
  'MONTHLY_LIMIT_REACHED',
  // Stale tip-config rejections (see useReservationCommit's TIP_STALE_CODES
  // via services/commit/shared.ts) — pre-payment business rejections, same
  // bucket as the three above, not the funnel's 'unknown' catch-all.
  'tip_policy_refresh',
  'invalid_tip_percent',
  // Anti-abuse refusals (same-slot duplicate cap, per-day booking cap,
  // captcha) — pre-payment business rejections, like the three at the top of
  // this set. Same concept as PRE_PAYMENT_BACKEND_CODES in
  // `@features/booking/services/commit/shared`, which additionally governs the
  // card token and the payment-failure notify.
  'SAME_SLOT_DUPLICATE',
  'DAILY_LIMIT_REACHED',
  'CAPTCHA_REQUIRED',
]);

/**
 * Max length we'll forward to GA when the message isn't a known code. Long
 * enough to keep debug context, short enough to bound PII risk if a future
 * thrower builds an Error from user input or backend payload data.
 */
const ERROR_CODE_MAX_LENGTH = 64;

/**
 * Which booking step hosted the commit click. Determines which leg of the
 * funnel the event belongs to.
 *
 * - `confirm` — the legacy Confirm step at /confirmation (pre-flag-flip)
 * - `guarantee` — the Guarantee step (post-flip, when fee > 0)
 * - `personalize` — the Personalize step (post-flip, when no guarantee fee)
 *
 * Other values are unexpected (mis-host or future step) and tagged 'unknown'
 * so the dashboard surfaces them rather than dropping the event.
 */
export type CommitSourceStep = 'confirm' | 'guarantee' | 'personalize' | 'unknown';

/**
 * Map an URL pathname to the corresponding source step. Centralized so the
 * hook + any test scaffolding agree on the mapping.
 *
 * Returns 'unknown' for unrecognized paths. Such events still land in GA
 * tagged as 'unknown' so the dashboard surfaces them — but we also emit a
 * Sentry breadcrumb so the drift is noticed at debug time, not weeks later
 * when someone notices a funnel row that doesn't fit.
 */
export function deriveSourceStepFromPath(pathname: string): CommitSourceStep {
  if (pathname === '/confirmation') return 'confirm';
  if (pathname === '/confirm-guarantee') return 'guarantee';
  if (pathname === '/personalize') return 'personalize';
  // Lazy import to avoid Sentry coupling in pure helpers; safe inside the
  // fallback branch.
  if (typeof window !== 'undefined') {
    import('@sentry/react').then((Sentry) => {
      Sentry.addBreadcrumb({
        category: 'commit-funnel',
        message: 'Unknown source_step pathname',
        level: 'warning',
        data: { pathname },
      });
    }).catch(() => { /* non-blocking */ });
  }
  return 'unknown';
}

/**
 * Card-decline phrasings the payment processors forward verbatim in the
 * response `errorMessage`. The backend passes the processor's raw message
 * through, so we recognize a decline by content and map it to
 * `card_declined` → COMMIT_ERROR_PAYMENT_DECLINED ("try another card"),
 * surfacing our own translated copy instead of the raw processor string.
 *
 * - MercadoPago: multi-language phrasings returned on a declined charge.
 * - Stripe: standard decline `error.message` strings (generic decline,
 *   insufficient funds, expired card, incorrect cvc/number, do-not-honor).
 */
const CARD_DECLINE_PATTERNS: RegExp[] = [
  // MercadoPago
  /no procesó el pago/i,
  /n[aã]o ter processado/i,
  /have not processed/i,
  /Customer not found/i,
  // Stripe
  /card was declined/i,
  /card is declined/i,
  /insufficient funds/i,
  /card has expired/i,
  /expired card/i,
  /(security code|cvc) is incorrect/i,
  /incorrect (cvc|security code|number)/i,
  /card number is incorrect/i,
  /do not honor/i,
];

/**
 * Categorize a commit error into a small set of buckets the dashboard can
 * pivot on. Keep the bucket list short — the goal is funnel monitoring, not
 * granular error analysis (use Sentry for that).
 */
export type CommitErrorCategory =
  | '3ds_rejected'
  | 'card_declined'
  | 'backend_blocked'    // RESTAURANT_INACTIVE, BLOCKED_USER, MONTHLY_LIMIT
  | 'network'            // fetch/timeout
  | 'unknown';

/**
 * Map a raw cause + ThreeDsError flag into a coarse category. `causeName` is
 * the `Error.name` (e.g. 'ThreeDsError'); `causeMessage` is `Error.message`.
 */
export function categorizeCommitError(opts: {
  causeName: string | undefined;
  causeMessage: string;
}): CommitErrorCategory {
  const { causeName, causeMessage } = opts;
  if (causeName === 'ThreeDsError') return '3ds_rejected';
  // Backend errors arrive as a raw Error with the code as the message
  // (see useReservationCommit's mapping table).
  if (KNOWN_BACKEND_ERROR_CODES.has(causeMessage)) return 'backend_blocked';
  // Card declines from MercadoPago or Stripe — recognized by message content
  // so the user gets COMMIT_ERROR_PAYMENT_DECLINED in their language.
  if (CARD_DECLINE_PATTERNS.some((re) => re.test(causeMessage))) {
    return 'card_declined';
  }
  if (/network|fetch|timeout|ECONNREFUSED|ECONNRESET/i.test(causeMessage)) return 'network';
  return 'unknown';
}

/**
 * Common attribution attached to every commit funnel event.
 */
export interface CommitFunnelAttribution {
  source_step: CommitSourceStep;
  guarantee_type: string;           // booking.guaranteeType or 'early_access'
  payment_processor: 'stripe' | 'mercadopago' | 'none';
  is_waiting_list: boolean;
  time_on_step_ms: number;          // hook mount → commit click
}

/**
 * Build a BOOKING_COMMIT_ATTEMPT event. Fire once per commit click, before
 * the API call.
 */
export function makeCommitAttemptEvent(attribution: CommitFunnelAttribution): AnalyticsEvent {
  return {
    name: ANALYTICS_EVENTS.BOOKING_COMMIT_ATTEMPT,
    properties: { ...attribution },
  };
}

/**
 * Constrain the `error_code` we forward to GA. Today's commit-error throwers
 * produce code-shaped strings (`'BLOCKED_USER_SUBTITLE'`,
 * `'RESTAURANT_INACTIVE'`, `'MONTHLY_LIMIT_REACHED'`, MP localized templates
 * without PII), but the path is a bare `Error.message` passthrough — a future
 * thrower that builds an Error from user input or a backend payload would
 * leak that string verbatim to Google Analytics.
 *
 * Strategy (hybrid):
 *   - `ThreeDsError` → its discrete `.code` field (already an enum)
 *   - Message in `KNOWN_BACKEND_ERROR_CODES` → passthrough (debug-friendly)
 *   - Any other `Error.message` → truncate to {@link ERROR_CODE_MAX_LENGTH}
 *   - Non-Error cause → undefined (drop the field entirely)
 *
 * The hook still routes the raw error to Sentry via `capturePaymentError`
 * — full context is preserved upstream of GA.
 */
export function safeErrorCode(cause: unknown): string | undefined {
  // Use duck-typing for ThreeDsError so we don't import a feature module from
  // a core util (and avoid the circular-dep risk). The `name === 'ThreeDsError'`
  // + `typeof code === 'string'` shape is sufficient and matches the class
  // exported from `@features/booking/hooks/handle3dsChallenge`.
  if (
    cause instanceof Error &&
    cause.name === 'ThreeDsError' &&
    typeof (cause as Error & { code?: unknown }).code === 'string'
  ) {
    return (cause as Error & { code: string }).code;
  }
  if (!(cause instanceof Error)) return undefined;
  if (KNOWN_BACKEND_ERROR_CODES.has(cause.message)) return cause.message;
  return cause.message.slice(0, ERROR_CODE_MAX_LENGTH);
}

/**
 * Build a BOOKING_COMMIT_FAILURE event. Fire from the hook's catch block,
 * after the CommitError unwrapping logic.
 */
export function makeCommitFailureEvent(
  attribution: CommitFunnelAttribution,
  errorOpts: {
    error_category: CommitErrorCategory;
    error_code?: string;
  },
): AnalyticsEvent {
  return {
    name: ANALYTICS_EVENTS.BOOKING_COMMIT_FAILURE,
    properties: { ...attribution, ...errorOpts },
  };
}
